import { Permission } from '@/api/permissions';

/**
 * Frontend mirror of the backend's `ENTITY_FIELD_UPDATE_PERMISSIONS_MAP`
 * (`libs/permissions/src/configs/entity-permissions.config.ts`), which
 * `Entity.update()` enforces field by field on every `PUT /entity/v1/entities/:id`.
 * Pinned by `./field-permissions.test.ts` against that file, so a field gaining
 * or losing its own permission there cannot silently skip the UI.
 *
 * A PUT-body field absent from this map carries no field-tier permission of its
 * own — `EntityManageProfile` alone governs it.
 */
export const ENTITY_FIELD_UPDATE_PERMISSIONS = {
  legalName: Permission.EntityUpdateSensitiveInfo,
  displayName: Permission.EntityManageProfile,
  identificationNumber: Permission.EntityUpdateSensitiveInfo,
  entityEmail: Permission.EntityUpdateSensitiveInfo,
  country: Permission.EntityUpdateSensitiveInfo,
  state: Permission.EntityUpdateSensitiveInfo,
  type: Permission.EntityUpdateSensitiveInfo,
  logoImageId: Permission.EntityManageProfile,
  registeredAt: Permission.EntityUpdateSensitiveInfo,
  registeredAddressCity: Permission.EntityUpdateSensitiveInfo,
  registeredAddress: Permission.EntityUpdateSensitiveInfo,
  registeredAddressPostalCode: Permission.EntityUpdateSensitiveInfo,
  shareStructure: Permission.EntityManageProfile,
} as const satisfies Record<string, Permission>;

export type EntityFieldWithPermission = keyof typeof ENTITY_FIELD_UPDATE_PERMISSIONS;

/**
 * Every field the entity profile PUT body carries, in the order an approver
 * reads them. `entityWebsite` is deliberately here but not in the permission
 * map — the diff must still report a change to it.
 */
export const ENTITY_PROFILE_FIELDS = [
  'legalName',
  'displayName',
  'identificationNumber',
  'entityEmail',
  'entityWebsite',
  'country',
  'state',
  'type',
  'registeredAt',
  'registeredAddressCity',
  'registeredAddress',
  'registeredAddressPostalCode',
  'logoImageId',
  'shareStructure',
] as const;

export type EntityProfileField = (typeof ENTITY_PROFILE_FIELDS)[number];

export const getEntityFieldPermission = (field: string): Permission | undefined =>
  (ENTITY_FIELD_UPDATE_PERMISSIONS as Record<string, Permission | undefined>)[field];
